import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Progress } from '@/components/ui/progress';
import { TileEmpty } from '../TileWrapper';
import { PieChart } from 'lucide-react';
import { cn } from '@/lib/utils';

// Mock data for simulation
const MOCK_DATA = {
  percentage: 78,
  valid: 156,
  expiring: 23,
  expired: 12,
  missing: 9,
  total: 200,
  projectCount: 5,
};

export function ComplianceOverviewTile({ filter, size, useMockData }) {
  const { company, userProfile } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (useMockData) {
      setData(MOCK_DATA);
      setLoading(false);
      return;
    }

    if (!company?.id || !userProfile?.id) return;

    const loadData = async () => {
      setLoading(true);
      try {
        const { data: dashData, error } = await supabase.rpc('get_dashboard_data_v2', {
          p_company_id: company.id,
          p_user_id: userProfile.id,
          p_include_archived: false,
          p_limit: 100,
          p_offset: 0,
          p_search: null,
        });

        if (error) throw error;

        // Filter by space if needed
        let projects = dashData?.projects || [];
        if (filter?.spaceId) {
          projects = projects.filter(p => p.id === filter.spaceId);
        }

        // Aggregate compliance data
        let totalValid = 0;
        let totalExpiring = 0;
        let totalExpired = 0;
        let totalMissing = 0;

        projects.forEach(p => {
          const summary = p.compliance_summary || {};
          totalValid += summary.valid || 0;
          totalExpiring += summary.expiring || 0;
          totalExpired += summary.expired || 0;
          totalMissing += summary.missing || 0;
        });

        const total = totalValid + totalExpiring + totalExpired + totalMissing;
        const percentage = total > 0 ? Math.round((totalValid / total) * 100) : 0;

        setData({
          percentage,
          valid: totalValid,
          expiring: totalExpiring,
          expired: totalExpired,
          missing: totalMissing,
          total,
          projectCount: projects.length,
        });
      } catch (err) {
        console.error('Failed to load compliance data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [company?.id, userProfile?.id, filter?.spaceId, useMockData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-20 h-20 rounded-full bg-gray-200 mb-3" />
          <div className="h-4 w-24 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (!data || data.total === 0) {
    return <TileEmpty message="No compliance data" icon={PieChart} />;
  }

  const getStatusColor = (pct) => {
    if (pct >= 90) return 'text-green-600';
    if (pct >= 70) return 'text-amber-600';
    return 'text-red-600';
  };

  const getProgressColor = (pct) => {
    if (pct >= 90) return 'bg-green-500';
    if (pct >= 70) return 'bg-amber-500';
    return 'bg-red-500';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Main percentage display */}
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className={cn('text-5xl font-bold', getStatusColor(data.percentage))}>
            {data.percentage}%
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Overall Compliance
          </p>
          <p className="text-xs text-gray-400">
            {data.projectCount} space{data.projectCount !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mt-auto">
        <div className="relative h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={cn('absolute h-full transition-all', getProgressColor(data.percentage))}
            style={{ width: `${data.percentage}%` }}
          />
        </div>

        {/* Stats row - only show in medium size */}
        {size !== 'small' && (
          <div className="grid grid-cols-4 gap-2 mt-3 text-center">
            <div className="bg-green-50 rounded p-1">
              <div className="text-sm font-semibold text-green-700">{data.valid}</div>
              <div className="text-xs text-green-600">Valid</div>
            </div>
            <div className="bg-amber-50 rounded p-1">
              <div className="text-sm font-semibold text-amber-700">{data.expiring}</div>
              <div className="text-xs text-amber-600">Expiring</div>
            </div>
            <div className="bg-red-50 rounded p-1">
              <div className="text-sm font-semibold text-red-700">{data.expired}</div>
              <div className="text-xs text-red-600">Expired</div>
            </div>
            <div className="bg-gray-50 rounded p-1">
              <div className="text-sm font-semibold text-gray-700">{data.missing}</div>
              <div className="text-xs text-gray-600">Missing</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
